import { useIsMobile } from "../lib/useIsMobile";
import { useState, useEffect, useRef, useCallback } from "react";
import { sb } from "../lib/supabase";

// In-browser softphone (Twilio Voice SDK). Mounted ONCE globally in App. It registers a
// Device with a short-lived token, exposes window.scoopDial(number, name) for the "Call"
// buttons, and shows a floating widget for incoming + active calls. Audio is in the browser.
export function Softphone() {
  const isMobile = useIsMobile();
  const [view, setView] = useState("idle");   // idle | incoming | dialing | active
  const [peer, setPeer] = useState("");
  const [muted, setMuted] = useState(false);
  const [secs, setSecs] = useState(0);
  const devRef = useRef(null);
  const callRef = useRef(null);
  const incomingRef = useRef(null);

  const endUI = useCallback(() => {
    callRef.current = null; incomingRef.current = null;
    setMuted(false); setSecs(0); setPeer(""); setView("idle");
  }, []);

  const bind = useCallback((call, who) => {
    callRef.current = call; setPeer(who || ""); setView("active");
    call.on("disconnect", endUI); call.on("error", endUI); call.on("cancel", endUI);
  }, [endUI]);

  useEffect(() => {
    let dead = false, refresh;
    (async () => {
      let Device;
      try { ({ Device } = await import("@twilio/voice-sdk")); } catch (e) { console.warn("[softphone] sdk load failed", e); return; }
      let tok;
      try { tok = await sb.fn("twilio-voice-token", {}); } catch { return; }
      if (!tok?.token || dead) return;
      const dev = new Device(tok.token, { logLevel: "error", codecPreferences: ["opus", "pcmu"] });
      devRef.current = dev;
      dev.on("error", (e) => console.warn("[softphone] device error", e?.message || e));
      dev.on("incoming", (call) => {
        incomingRef.current = call;
        setPeer(prettyFrom(call)); setView("incoming");
        call.on("cancel", endUI); call.on("disconnect", endUI);
      });
      try { await dev.register(); } catch (e) { console.warn("[softphone] register failed", e); }

      window.scoopDial = async (number, name) => {
        if (!number || callRef.current || incomingRef.current) return;
        setView("dialing"); setPeer(name || number);
        try { const call = await dev.connect({ params: { To: String(number) } }); bind(call, name || number); }
        catch (e) { console.warn("[softphone] dial failed", e); endUI(); }
      };
      // tokens last ~1h; refresh well before expiry
      refresh = setInterval(async () => {
        try { const t = await sb.fn("twilio-voice-token", {}); if (t?.token) dev.updateToken(t.token); } catch { /* */ }
      }, 50 * 60 * 1000);
    })();
    return () => { dead = true; clearInterval(refresh); try { window.scoopDial = undefined; } catch {} try { devRef.current?.destroy(); } catch {} };
  }, [bind, endUI]);

  useEffect(() => {
    if (view !== "active") return;
    const t = setInterval(() => setSecs((s) => s + 1), 1000);
    return () => clearInterval(t);
  }, [view]);

  function accept() { const c = incomingRef.current; if (!c) return; try { c.accept(); } catch {} bind(c, peer); incomingRef.current = null; }
  function reject() { const c = incomingRef.current; if (!c) return; try { c.reject(); } catch {} endUI(); }
  function hangup() { try { callRef.current?.disconnect(); } catch {} endUI(); }
  function toggleMute() { const c = callRef.current; if (!c) return; const m = !muted; try { c.mute(m); } catch {} setMuted(m); }

  if (view === "idle") return null;
  const fmt = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

  return (
    <div style={isMobile ? S.wrapMobile : S.wrap}>
      <div style={{ fontSize: 12, color: "var(--text2)", letterSpacing: ".02em" }}>
        {view === "incoming" ? "📞 Incoming call" : view === "dialing" ? "Calling…" : "● On call"}
      </div>
      <div style={{ fontSize: 17, fontWeight: 600, color: "var(--text)", marginTop: 3, overflowWrap: "anywhere" }}>{peer || "Unknown"}</div>
      {view === "active" && <div style={{ fontSize: 13, color: "var(--text2)", marginTop: 2, fontVariantNumeric: "tabular-nums" }}>{fmt(secs)}</div>}
      <div style={{ display: "flex", gap: 8, marginTop: 13 }}>
        {view === "incoming" ? (<>
          <button style={S.green} onClick={accept}>Accept</button>
          <button style={S.red} onClick={reject}>Decline</button>
        </>) : (<>
          {view === "active" && <button style={S.gray} onClick={toggleMute}>{muted ? "🔇 Unmute" : "🎙 Mute"}</button>}
          <button style={S.red} onClick={hangup}>{view === "dialing" ? "Cancel" : "Hang up"}</button>
        </>)}
      </div>
    </div>
  );
}

function prettyFrom(call) {
  const f = call?.parameters?.From || "";
  if (f.startsWith("client:")) return f.slice(7);
  const d = f.replace(/\D/g, "");
  if (d.length === 11 && d[0] === "1") return `(${d.slice(1, 4)}) ${d.slice(4, 7)}-${d.slice(7)}`;
  if (d.length === 10) return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
  return f || "Unknown";
}

const baseBtn = { border: "none", borderRadius: 999, padding: "9px 18px", fontSize: 13, fontWeight: 600, cursor: "pointer", color: "#fff", minHeight: 40 };
const S = {
  wrap: { position: "fixed", right: 20, bottom: 20, zIndex: 9999, background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 16, boxShadow: "var(--shadow-lg)", padding: "16px 18px", minWidth: 226 },
  // Phone: a full-width strip right under the sticky topbar. The floating bottom-right card
  // sat exactly where every bottom-sheet's primary button lives (Save / Charge) at z 9999,
  // so you could not finish a form while on a call. z 90 keeps sheets (z 100+) above it.
  wrapMobile: { position: "fixed", left: 8, right: 8, top: 66, zIndex: 90, background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 14, boxShadow: "var(--shadow-lg)", padding: "12px 14px" },
  green: { ...baseBtn, background: "var(--ok)", flex: 1 },
  red: { ...baseBtn, background: "var(--danger)", flex: 1 },
  gray: { ...baseBtn, background: "var(--text2)" },
};
